/*
 * 模块: Design Tokens - Colors
 * 职责: 颜色系统定义
 * 依赖: 无
 * 导出: colors
 * 限制: 文件<280行
 */

/**
 * 品牌主色系 (Indigo & Violet)
 * 使用更深邃、高级的靛蓝色作为主色
 */
export const brandColors = {
  primary: {
    lighter: '#e0e7ff', // Indigo 100
    light: '#818cf8',   // Indigo 400
    main: '#4f46e5',    // Indigo 600
    dark: '#4338ca',    // Indigo 700
    darker: '#312e81',  // Indigo 900
  },
  secondary: {
    lighter: '#fce7f3', // Pink 100
    light: '#f472b6',   // Pink 400
    main: '#ec4899',    // Pink 500
    dark: '#db2777',    // Pink 600
    darker: '#831843',  // Pink 900
  },
} as const;

/**
 * 功能色系 (Modern & Vibrant)
 */
export const functionalColors = {
  success: {
    lighter: '#dcfce7',
    light: '#4ade80',
    main: '#10b981', // Emerald 500
    dark: '#059669',
    darker: '#064e3b',
  },
  warning: {
    lighter: '#fef3c7',
    light: '#fbbf24',
    main: '#f59e0b', // Amber 500
    dark: '#d97706',
    darker: '#78350f',
  },
  error: {
    lighter: '#fee2e2',
    light: '#f87171',
    main: '#ef4444', // Red 500
    dark: '#b91c1c',
    darker: '#7f1d1d',
  },
  info: {
    lighter: '#e0f2fe',
    light: '#38bdf8',
    main: '#0ea5e9', // Sky 500
    dark: '#0284c7',
    darker: '#0c4a6e',
  },
} as const;

/**
 * 中性色系 (Slate - Cool Gray)
 * 比纯灰更具科技感
 */
export const neutralColors = {
  white: '#ffffff',
  gray: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  black: '#020617',
} as const;

/**
 * 数据可视化色板 (High Contrast & Harmony)
 */
export const dataVizColors = {
  categorical: [
    '#4f46e5', // Indigo
    '#10b981', // Emerald
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#ec4899', // Pink
    '#0ea5e9', // Sky
    '#8b5cf6', // Violet
    '#14b8a6', // Teal
  ],
  sequential: {
    blue: ['#eff6ff', '#dbeafe', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#2563eb', '#1d4ed8'],
    green: ['#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d'],
    orange: ['#fff7ed', '#ffedd5', '#fed7aa', '#fdba74', '#fb923c', '#f97316', '#ea580c', '#c2410c'],
    purple: ['#faf5ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7e22ce'],
  },
  diverging: {
    bluePurple: ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe', '#e0e7ff', '#eef2ff', '#ffffff'],
    greenOrange: ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5', '#ecfccb', '#fef9c3', '#fffbeb'],
  },
} as const;

/**
 * 状态色
 */
export const statusColors = {
  pending: '#f59e0b',    // Amber
  processing: '#3b82f6', // Blue
  success: '#10b981',    // Emerald
  failed: '#ef4444',     // Red
  warning: '#f97316',    // Orange
  disabled: '#94a3b8',   // Slate 400
} as const;

/**
 * 渐变色 (Smooth & Modern)
 */
export const gradients = {
  primary: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)', // Indigo to Violet
  success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)',
  info: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)',
  sunset: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
  ocean: 'linear-gradient(135deg, #0ea5e9 0%, #4f46e5 100%)',
  royal: 'linear-gradient(135deg, #4f46e5 0%, #ec4899 100%)', // Indigo to Pink
} as const;

/**
 * 背景色
 */
export const backgroundColors = {
  page: '#f8fafc', // Slate 50
  card: '#ffffff',
  hover: 'rgba(79, 70, 229, 0.04)',
  selected: 'rgba(79, 70, 229, 0.08)',
  disabled: '#f1f5f9',
  overlay: 'rgba(15, 23, 42, 0.6)', // Darker overlay
} as const;

/**
 * 导出所有颜色
 */
export const colors = {
  brand: brandColors,
  functional: functionalColors,
  neutral: neutralColors,
  dataViz: dataVizColors,
  status: statusColors,
  gradients,
  background: backgroundColors,
} as const;

export default colors;
